import type { L10n, Locale, SportProfileField } from '../types'

/** Whole months elapsed since an ISO date (yyyy-mm-dd). Negative/invalid → null. */
export function monthsSince(iso: string, now: Date = new Date()): number | null {
  if (!iso) return null
  const start = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(start.getTime()) || start > now) return null
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth())
  if (now.getDate() < start.getDate()) months -= 1
  return Math.max(0, months)
}

/** "2 anos e 6 meses" / "2 years and 6 months". */
export function formatTenure(months: number, locale: Locale): string {
  const years = Math.floor(months / 12)
  const rest = months % 12
  const pt = locale === 'pt-BR'
  const y = years === 1 ? (pt ? '1 ano' : '1 year') : `${years} ${pt ? 'anos' : 'years'}`
  const m = rest === 1 ? (pt ? '1 mês' : '1 month') : `${rest} ${pt ? 'meses' : 'months'}`
  if (years === 0) return m
  if (rest === 0) return y
  return `${y} ${pt ? 'e' : 'and'} ${m}`
}

const recordWords: Record<Locale, Record<string, [string, string]>> = {
  'pt-BR': {
    'boxing:wins': ['vitória', 'vitórias'],
    'boxing:losses': ['derrota', 'derrotas'],
    'boxing:draws': ['empate', 'empates'],
  },
  'en-US': {
    'boxing:wins': ['win', 'wins'],
    'boxing:losses': ['loss', 'losses'],
    'boxing:draws': ['draw', 'draws'],
  },
}

/**
 * Grouped record text, e.g. "6 vitórias · 1 derrota · 1 empate".
 * `values` maps member metric id → number; members are listed in catalogue order.
 */
export function formatMetricGroup(
  memberIds: string[],
  values: Record<string, number>,
  locale: Locale
): string {
  return memberIds
    .map((id) => {
      const n = values[id] ?? 0
      const [one, many] = recordWords[locale][id] ?? [id, id]
      return `${n} ${n === 1 ? one : many}`
    })
    .join(' · ')
}

/**
 * Display text for a categorical/profile field, or '' when not provided.
 * Tenure fields are derived from their date field and never entered manually.
 */
export function displaySportField(
  field: SportProfileField,
  values: Record<string, string>,
  locale: Locale,
  tl: (value: L10n) => string
): string {
  if (field.type === 'tenure') {
    const months = monthsSince(values[field.from] ?? '')
    return months === null ? '' : formatTenure(months, locale)
  }
  const raw = values[field.id] ?? ''
  if (!raw) return ''
  if (field.type === 'select') {
    const option = field.options.find((item) => item.id === raw)
    return option ? tl(option.label) : ''
  }
  if (field.type === 'date') {
    const date = new Date(`${raw}T00:00:00`)
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' })
  }
  return raw
}
